<template>
    <DynamicScroller
        ref="scroller"
        key-field="feId"
        :items="scrollerItems"
        :min-item-size="60"
        class="EvieChatThread"
        :class="{ 'EvieChatThread--settled': isSettled }"
    >
        <template #default="{ item, active, index }">
            <DynamicScrollerItem
                :item="item"
                :active="active"
                :index="index"
                :size-dependencies="[item.content, item.errorType, streamingMessage]"
            >
                <div v-if="item.feId === '__spacer__'" class="EvieChatThread__spacer" :style="{ height: spacerHeight + 'px' }" />
                <div v-else class="EvieChatThread__itemRow">
                    <EvieChatThinking v-if="item.feId === '__thinking__'" :visible="true" />
                    <div v-else-if="item.feId === '__streaming__'" class="EvieChatThread__streamingBubble">
                        <EvieChatMessageMarkdown :text="streamingMessage || ''" />
                    </div>
                    <EvieChatMessage
                        v-else
                        :message="item"
                        :is-last="index === lastRealMessageIndex && sendStatus !== 'loading'"
                        :is-settled="isSettled"
                        @retry="$emit('retry', $event)"
                        @new-chat="$emit('new-chat')"
                        @feedback="$emit('feedback', $event)"
                    />
                </div>
            </DynamicScrollerItem>
        </template>
    </DynamicScroller>
</template>

<style lang="scss">
// Hidden until isSettled so the 8-frame scroll-to-bottom loop runs invisibly.
// Without this, switching conversations shows a visible "fly to bottom" as
// virtual items render and scrollTop is driven up over ~130ms.
.EvieChatThread {
    height: 100%;
    padding: 24px 0 16px;
    box-sizing: border-box;
    opacity: 0;
    transition: opacity 0.15s ease;

    &--settled {
        opacity: 1;
    }
}

.EvieChatThread__itemRow {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 16px;
}

.EvieChatThread__streamingBubble {
    max-width: 90%;
    padding: 4px 0;
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    // PRODUCT-26110 Task 8b Step 5b: vue-virtual-scroller 1 (Vue-2-only; DynamicScrollerItem's
    // internal mixin calls `this.vscrollParent.$on/$off('vscroll:update', ...)`, the
    // INSTANCE_EVENT_EMITTER compat trigger Task 5 deferred) -> 2, the Vue 3 release line
    // (confirmed zero `$on`/`$off` in its dist bundle). Same `DynamicScroller`/
    // `DynamicScrollerItem` export names, same props (`items`/`key-field`/`min-item-size`/
    // `size-dependencies`) and default-slot shape (`{ item, active, index }`), so no template
    // changes were needed here -- only the dependency bump. The hand-rolled
    // `src/vue-virtual-scroller.d.ts` ambient module shim (needed because v1 shipped without
    // its own types) was removed: v2 ships real generic-typed declarations
    // (`dist/index.d.ts`), which the shim would otherwise shadow.
    import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
    import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

    import { EvieThreadMessage } from '@/modules/evie-ai/models/EvieThreadMessage.model';

    import EvieChatMessage from '../EvieChatMessage/EvieChatMessage.vue';
    import EvieChatMessageMarkdown from '../EvieChatMessage/sub-components/EvieChatMessageMarkdown.vue';
    import EvieChatThinking from '../EvieChatThinking/EvieChatThinking.vue';

    interface ThinkingItem { feId: '__thinking__' }
    interface StreamingItem { feId: '__streaming__' }
    interface SpacerItem { feId: '__spacer__' }
    type ScrollerItem = EvieThreadMessage | ThinkingItem | StreamingItem | SpacerItem;

    const THINKING_ITEM: ThinkingItem = { feId: '__thinking__' };
    const STREAMING_ITEM: StreamingItem = { feId: '__streaming__' };
    const SPACER_ITEM: SpacerItem = { feId: '__spacer__' };
    const BOTTOM_THRESHOLD = 40;

    export default defineComponent({
        name: 'EvieChatThread',
        components: {
            DynamicScroller, DynamicScrollerItem, EvieChatMessage, EvieChatThinking, EvieChatMessageMarkdown,
        },
        props: {
            messageIds: { type: Array as PropType<string[]>, required: true },
            messagesById: { type: Object as PropType<Record<string, EvieThreadMessage>>, required: true },
            sendStatus: { type: String as PropType<'idle' | 'loading'>, required: true },
            spacerHeight: { type: Number, default: 96 },
            streamingMessage: { type: String as PropType<string | null>, default: null },
        },
        emits: [ 'feedback', 'new-chat', 'retry', 'scroll-state-change' ],
        data() {
            return {
                // False on every mount; set to true once the initial scroll-to-bottom
                // loop completes. Controls two things simultaneously:
                //   1. Scroller opacity (hidden while positioning, revealed after)
                //   2. Copy button visibility (prevents flash on first render)
                // Only the mounted() flow sets this via the onComplete callback.
                // Subsequent scrollToBottom() calls from watchers pass no callback
                // and leave isSettled untouched (it's already true at that point).
                isSettled: false,
                // True when the user has manually scrolled up away from the bottom.
                // Suppresses per-token scroll; only cleared by scrollToBottom() so that
                // sending a new message or clicking jump re-engages auto-scroll.
                isUserScrolledUp: false,
            };
        },
        computed: {
            scrollerItems(): ScrollerItem[] {
                const items: ScrollerItem[] = this.messageIds
                    .map((id) => this.messagesById[id])
                    .filter(Boolean);
                if (this.sendStatus === 'loading') {
                    // streamingMessage starts as '' (empty string) when request fires but before
                    // the first token arrives. Use truthy check so LineLoader shows during that
                    // gap, switching to the streaming bubble only once text actually accumulates.
                    items.push(this.streamingMessage ? STREAMING_ITEM : THINKING_ITEM);
                }
                items.push(SPACER_ITEM);
                return items;
            },
            lastRealMessageIndex(): number {
                const items = this.scrollerItems;
                for (let i = items.length - 1; i >= 0; i--) {
                    const feId = items[i].feId;
                    if (feId !== '__thinking__' && feId !== '__streaming__' && feId !== '__spacer__') {
                        return i;
                    }
                }
                return -1;
            },
        },
        watch: {
            streamingMessage(newVal: string | null, oldVal: string | null) {
                if (newVal !== null) {
                    // New stream starting — clear any scroll lock from a prior interaction.
                    if (oldVal === null) {
                        this.isUserScrolledUp = false;
                    }
                    if (!this.isUserScrolledUp) {
                        const el = (this.$refs.scroller as any)?.$el as HTMLElement | undefined;
                        if (el) {
                            el.scrollTop = el.scrollHeight;
                        }
                    }
                }
            },
        },
        created() {
            // Last observed scrollTop — used in onScroll to detect direction.
            // Non-reactive: changes every scroll event, no template dependency.
            (this as any)._lastScrollTop = 0;
        },
        mounted() {
            // .native modifier is removed in Vue 3; listen on the scroller's root
            // element directly instead (same target the modifier bound to).
            const el = (this.$refs.scroller as any)?.$el as HTMLElement | undefined;
            el?.addEventListener('scroll', this.onScroll, { passive: true });
            this.$nextTick(() => {
                this.scrollToBottom(() => {
                    this.isSettled = true;
                });
            });
        },
        beforeUnmount() {
            const el = (this.$refs.scroller as any)?.$el as HTMLElement | undefined;
            el?.removeEventListener('scroll', this.onScroll);
        },
        methods: {
            // Used by mounted() and EvieChatConversationPanel via $refs.
            // Runs an 8-frame rAF loop so each frame catches the updated scrollHeight
            // as DynamicScroller measures virtual items — needed on mount/convo-switch
            // but overkill (and dangerous at high token rates) for per-token scrolls.
            scrollToBottom(onComplete?: () => void) {
                this.isUserScrolledUp = false;
                const el = (this.$refs.scroller as any)?.$el as HTMLElement | undefined;
                if (!el) {
                    onComplete?.();
                    return;
                }
                let frames = 8;
                const scroll = () => {
                    el.scrollTop = el.scrollHeight;
                    if (--frames > 0) {
                        requestAnimationFrame(scroll);
                    } else {
                        onComplete?.();
                    }
                };
                requestAnimationFrame(scroll);
            },
            onScroll(e: Event) {
                const el = e.currentTarget as HTMLElement;
                const prevScrollTop = (this as any)._lastScrollTop;
                (this as any)._lastScrollTop = el.scrollTop;
                const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD;
                // Lock only when scrollTop decreases AND we have moved away from the bottom.
                // The "!isAtBottom" guard prevents false positives from the rAF loop (where
                // DynamicScroller may shrink scrollHeight mid-loop, momentarily decreasing
                // scrollTop) and from any internal scroller adjustments at the bottom position.
                if (el.scrollTop < prevScrollTop && !isAtBottom) {
                    this.isUserScrolledUp = true;
                }
                this.$emit('scroll-state-change', isAtBottom);
            },
        },
    });
</script>
